import {
  PROMPT_BRIDGE_ASSISTANT_AVAILABILITY_TAG,
  promptBridgeApi,
  invalidateOnSuccess,
} from '@dms/api/base';

import {
  assistantAvailabilityResponseSchema,
  type AssistantAvailabilityResponse,
} from '@prompt-bridge/shared/ai-assistant-core';
import {
  ComposeMessageRequest,
  composeMessageRequestSchema,
  ComposeMessageResponse,
  composeMessageResponseSchema,
  EnhanceMessageRequest,
  enhanceMessageRequestSchema,
  EnhanceMessageResponse,
  enhanceMessageResponseSchema,
  ExtractImagesFromZipRequest,
  extractImagesFromZipRequestSchema,
  ExtractImagesFromZipResponse,
  extractImagesFromZipResponseSchema,
  type HondaStorageFileRequest,
  hondaStorageFileRequestSchema,
  type AuthorizationProfileQuery,
  authorizationProfileQuerySchema,
  type ImportHondaPartsPriceListResponse,
  importHondaPartsPriceListResponseSchema,
  type ImportHondaRecallResponse,
  importHondaRecallResponseSchema,
  type GetHondaPartRequest,
  getHondaPartRequestSchema,
  type HondaCatalogPart,
  hondaCatalogPartSchema,
  type GetHondaRecallsByVinRequest,
  getHondaRecallsByVinRequestSchema,
  type HondaRecallsByVinResponse,
  hondaRecallsByVinResponseSchema,
  type HondaMonthlyReportGenerationRequest,
  hondaMonthlyReportGenerationRequestSchema,
  type HondaMonthlyReportResponse,
  hondaMonthlyReportResponseSchema,
} from '@prompt-bridge/shared/validations';

import {getCompletedImporterCatalogTags} from './getCompletedImporterCatalogTags';
import {getUiLanguageHeader} from './getUiLanguageHeader';
import {
  CreateIssueReportRequestBody,
  createIssueReportRequestBodySchema,
  CreateIssueReportResponse,
  createIssueReportResponseSchema,
  ExtractDeliveryNoteDocumentRequest,
  extractDeliveryNoteDocumentRequestSchema,
  ExtractDeliveryNoteDocumentResponse,
  extractDeliveryNoteDocumentResponseSchema,
  GetMessageByIdRequest,
  getMessageByIdRequestSchema,
  Message,
  MessageResponse,
  messageResponseSchema,
  GetListMessagesRequest,
  getListMessagesRequestSchema,
  GetListMessagesResponse,
  getListMessagesResponseSchema,
  ScanVINRequest,
  scanVINRequestSchema,
  ScanVINResponse,
  scanVINResponseSchema,
  StorageUploadUrlRequest,
  storageUploadUrlRequestSchema,
  StorageUploadResponse,
  storageUploadResponseSchema,
  StoragePublicUrlRequest,
  storagePublicUrlRequestSchema,
  StoragePublicUrlResponse,
  storagePublicUrlResponseSchema,
  StorageUploadFileRequest,
  storageUploadFileRequestSchema,
  StoragePdfConvertRequest,
  storagePdfConvertRequestSchema,
  StoragePdfConvertResponse,
  storagePdfConvertResponseSchema,
  StorageFileMetadataRequest,
  storageFileMetadataRequestSchema,
  StorageFileMetadataResponse,
  storageFileMetadataResponseSchema,
  StorageOriginalFileRequest,
  storageOriginalFileRequestSchema,
  ImportNissanPriceListRequest,
  importNissanPriceListRequestSchema,
  ImportNissanPriceListResponse,
  importNissanPriceListResponseSchema,
} from './types';

export const promptBridgeExtendedApi = promptBridgeApi.injectEndpoints({
  endpoints: (build) => ({
    getAssistantAvailability: build.query<AssistantAvailabilityResponse, void>({
      query: () => ({
        url: '/api/v1/assistant/availability',
        method: 'GET',
      }),
      providesTags: [PROMPT_BRIDGE_ASSISTANT_AVAILABILITY_TAG],
      extraOptions: {
        responseSchema: assistantAvailabilityResponseSchema,
      },
    }),
    createIssueReport: build.mutation<CreateIssueReportResponse, CreateIssueReportRequestBody>({
      query: (queryArg) => ({
        url: '/api/v1/issue-reports',
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: createIssueReportRequestBodySchema,
        responseSchema: createIssueReportResponseSchema,
      },
    }),
    extractDeliveryNoteDocument: build.mutation<
      ExtractDeliveryNoteDocumentResponse,
      ExtractDeliveryNoteDocumentRequest
    >({
      query: (queryArg) => ({
        url: `/api/v1/openai/extractDeliveryNoteDocument`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeMessage'}],
      extraOptions: {
        requestSchema: extractDeliveryNoteDocumentRequestSchema,
        responseSchema: extractDeliveryNoteDocumentResponseSchema,
      },
    }),
    composeMessage: build.mutation<ComposeMessageResponse, ComposeMessageRequest>({
      query: (queryArg) => ({
        url: `/api/v1/openai/compose-message`,
        method: 'POST',
        body: queryArg,
        // Prompt Bridge forwards the UI language to DMS, which labels the record facts in it.
        // Without it the browser's own language would win. It is not the message `locale`.
        headers: getUiLanguageHeader(),
        // The base query validates `queryArg` against `requestSchema`; without it the check runs
        // on `undefined` and reports a parsing warning for every request.
        queryArg,
      }),
      extraOptions: {
        requestSchema: composeMessageRequestSchema,
        responseSchema: composeMessageResponseSchema,
      },
    }),
    enhanceMessage: build.mutation<EnhanceMessageResponse, EnhanceMessageRequest>({
      query: (queryArg) => ({
        url: `/api/v1/openai/enhance-message`,
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: enhanceMessageRequestSchema,
        responseSchema: enhanceMessageResponseSchema,
      },
    }),
    scanVIN: build.mutation<ScanVINResponse, ScanVINRequest>({
      query: (queryArg) => ({
        url: `/api/v1/openai/scanVIN`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeMessage'}],
      extraOptions: {
        requestSchema: scanVINRequestSchema,
        responseSchema: scanVINResponseSchema,
      },
    }),
    getMessages: build.query<GetListMessagesResponse, GetListMessagesRequest>({
      query: (queryArg) => ({
        url: `/api/v1/message/list`,
        method: 'GET',
        params: queryArg,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((message: Message) => ({
                type: 'PromptBridgeMessage' as const,
                id: message.id,
              })),
              {type: 'PromptBridgeMessage'},
            ]
          : [{type: 'PromptBridgeMessage'}],
      onQueryStarted: async (_arg, {dispatch, getCacheEntry, queryFulfilled}) => {
        const previousMessages = getCacheEntry().data;
        const result = await queryFulfilled.catch(() => undefined);
        if (result) {
          dispatch(
            promptBridgeApi.util.invalidateTags(
              getCompletedImporterCatalogTags(result.data, previousMessages)
            )
          );
        }
      },
      extraOptions: {
        requestSchema: getListMessagesRequestSchema,
        responseSchema: getListMessagesResponseSchema,
      },
    }),
    getMessageById: build.query<MessageResponse, GetMessageByIdRequest>({
      query: (queryArg) => ({
        url: `/api/v1/message/${queryArg.id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'PromptBridgeMessage', id: queryArg.id}],
      onQueryStarted: async (_arg, {dispatch, getCacheEntry, queryFulfilled}) => {
        const previousMessage = getCacheEntry().data;
        const result = await queryFulfilled.catch(() => undefined);
        if (result?.data.status !== 'SUCCESS') {
          return;
        }
        dispatch(
          promptBridgeApi.util.invalidateTags(
            getCompletedImporterCatalogTags([result.data], previousMessage ? [previousMessage] : [])
          )
        );
      },
      extraOptions: {
        requestSchema: getMessageByIdRequestSchema,
        responseSchema: messageResponseSchema,
      },
    }),
    retryMessage: build.mutation<MessageResponse, GetMessageByIdRequest>({
      query: (queryArg) => ({
        url: `/api/v1/message/${queryArg.id}/retry`,
        method: 'POST',
        // The base query validates `queryArg` against `requestSchema`; without it the check runs
        // on `undefined` and reports a parsing warning for every request.
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: GetMessageByIdRequest) => [
        {type: 'PromptBridgeMessage', id: queryArg.id},
      ]),
      extraOptions: {
        requestSchema: getMessageByIdRequestSchema,
        responseSchema: messageResponseSchema,
      },
    }),
    uploadFromUrl: build.mutation<StorageUploadResponse, StorageUploadUrlRequest>({
      query: (queryArg) => ({
        url: `/api/v1/storage/upload-from-url`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeStorageService'}],
      extraOptions: {
        requestSchema: storageUploadUrlRequestSchema,
        responseSchema: storageUploadResponseSchema,
      },
    }),
    getPublicDownloadUrl: build.query<
      StoragePublicUrlResponse,
      {fileId: string} & StoragePublicUrlRequest
    >({
      query: (queryArg) => ({
        url: `/api/v1/storage/download-url/${queryArg.fileId}`,
        method: 'GET',
        params: {
          namespace: queryArg.namespace,
          filename: queryArg.filename,
          variantId: queryArg.variantId,
          expirationSeconds: queryArg.expirationSeconds,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PromptBridgeStorageService', id: queryArg.fileId},
      ],
      extraOptions: {
        requestSchema: storagePublicUrlRequestSchema,
        responseSchema: storagePublicUrlResponseSchema,
      },
    }),
    uploadFile: build.mutation<StorageUploadResponse, {file: File} & StorageUploadFileRequest>({
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('file', queryArg.file);
        if (queryArg.namespace) {
          formData.append('namespace', queryArg.namespace);
        }
        if (queryArg.filename) {
          formData.append('filename', queryArg.filename);
        }
        if (queryArg.webhookUrls) {
          formData.append('webhookUrls', queryArg.webhookUrls);
        }
        if (queryArg.deleteAt) {
          formData.append('deleteAt', queryArg.deleteAt);
        }

        return {
          url: `/api/v1/storage/upload-file`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [{type: 'PromptBridgeStorageService'}],
      extraOptions: {
        requestSchema: storageUploadFileRequestSchema,
        responseSchema: storageUploadResponseSchema,
      },
    }),
    convertPdfToImages: build.mutation<StoragePdfConvertResponse, StoragePdfConvertRequest>({
      query: (queryArg) => ({
        url: `/api/v1/storage/convert-pdf-to-images`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeStorageService'}],
      extraOptions: {
        requestSchema: storagePdfConvertRequestSchema,
        responseSchema: storagePdfConvertResponseSchema,
      },
    }),
    getFileMetadata: build.query<
      StorageFileMetadataResponse,
      {fileId: string} & StorageFileMetadataRequest
    >({
      query: (queryArg) => ({
        url: `/api/v1/storage/files/${queryArg.fileId}/metadata`,
        method: 'GET',
        params: {
          namespace: queryArg.namespace,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PromptBridgeStorageService', id: queryArg.fileId},
      ],
      extraOptions: {
        requestSchema: storageFileMetadataRequestSchema,
        responseSchema: storageFileMetadataResponseSchema,
      },
    }),
    getOriginalFileUrl: build.query<string, {fileId: string} & StorageOriginalFileRequest>({
      query: (queryArg) => ({
        url: `/api/v1/storage/files/${queryArg.fileId}/original`,
        method: 'GET',
        params: {
          namespace: queryArg.namespace,
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob.size ? URL.createObjectURL(blob) : null;
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PromptBridgeStorageService', id: queryArg.fileId},
      ],
      extraOptions: {
        requestSchema: storageOriginalFileRequestSchema,
        // No response schema for binary data
      },
    }),
    extractImagesFromZip: build.query<ExtractImagesFromZipResponse, ExtractImagesFromZipRequest>({
      query: (queryArg) => ({
        url: '/api/v1/storage/extract-images-from-zip',
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: extractImagesFromZipRequestSchema,
        responseSchema: extractImagesFromZipResponseSchema,
      },
    }),
    importNissanPriceList: build.mutation<
      ImportNissanPriceListResponse,
      ImportNissanPriceListRequest
    >({
      query: (queryArg) => ({
        url: '/api/v1/nissan/import-price-list',
        queryArg,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['PromptBridgeMessage']),
      extraOptions: {
        requestSchema: importNissanPriceListRequestSchema,
        responseSchema: importNissanPriceListResponseSchema,
      },
    }),
    importHondaPartsPriceList: build.mutation<
      ImportHondaPartsPriceListResponse,
      HondaStorageFileRequest
    >({
      query: (body) => ({
        url: '/api/v1/honda/parts-price-list-import',
        method: 'POST',
        body,
        queryArg: body,
      }),
      invalidatesTags: invalidateOnSuccess(['PromptBridgeMessage']),
      extraOptions: {
        requestSchema: hondaStorageFileRequestSchema,
        responseSchema: importHondaPartsPriceListResponseSchema,
      },
    }),
    importHondaRecall: build.mutation<ImportHondaRecallResponse, HondaStorageFileRequest>({
      query: (body) => ({url: '/api/v1/honda/recall-import', method: 'POST', body, queryArg: body}),
      invalidatesTags: invalidateOnSuccess(['PromptBridgeMessage']),
      extraOptions: {
        requestSchema: hondaStorageFileRequestSchema,
        responseSchema: importHondaRecallResponseSchema,
      },
    }),
    getHondaPart: build.query<HondaCatalogPart, GetHondaPartRequest & AuthorizationProfileQuery>({
      query: (queryArg) => ({
        url: `/api/v1/honda/parts/${encodeURIComponent(queryArg.partNumber)}`,
        params: {authorizationProfileId: queryArg.authorizationProfileId},
        queryArg,
      }),
      providesTags: ['PromptBridgeHondaPart'],
      extraOptions: {
        requestSchema: getHondaPartRequestSchema.and(authorizationProfileQuerySchema),
        responseSchema: hondaCatalogPartSchema,
      },
    }),
    getHondaRecallsByVin: build.query<
      HondaRecallsByVinResponse,
      GetHondaRecallsByVinRequest & AuthorizationProfileQuery
    >({
      query: (queryArg) => ({
        url: `/api/v1/honda/recalls/${encodeURIComponent(queryArg.vin)}`,
        params: {authorizationProfileId: queryArg.authorizationProfileId},
        queryArg,
      }),
      providesTags: ['PromptBridgeHondaRecall'],
      extraOptions: {
        requestSchema: getHondaRecallsByVinRequestSchema.and(authorizationProfileQuerySchema),
        responseSchema: hondaRecallsByVinResponseSchema,
      },
    }),
    generateHondaMonthlyReport: build.mutation<
      HondaMonthlyReportResponse,
      HondaMonthlyReportGenerationRequest
    >({
      query: (body) => ({
        url: '/api/v1/honda/monthly-report',
        method: 'POST',
        body,
        queryArg: body,
      }),
      extraOptions: {
        requestSchema: hondaMonthlyReportGenerationRequestSchema,
        responseSchema: hondaMonthlyReportResponseSchema,
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAssistantAvailabilityQuery,
  useComposeMessageMutation,
  useCreateIssueReportMutation,
  useEnhanceMessageMutation,
  useExtractDeliveryNoteDocumentMutation,
  useScanVINMutation,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useGetMessageByIdQuery,
  useLazyGetMessageByIdQuery,
  useRetryMessageMutation,
  useUploadFromUrlMutation,
  useGetPublicDownloadUrlQuery,
  useLazyGetPublicDownloadUrlQuery,
  useUploadFileMutation,
  useConvertPdfToImagesMutation,
  useGetFileMetadataQuery,
  useLazyGetFileMetadataQuery,
  useGetOriginalFileUrlQuery,
  useLazyGetOriginalFileUrlQuery,
  useExtractImagesFromZipQuery,
  useImportNissanPriceListMutation,
  useImportHondaPartsPriceListMutation,
  useImportHondaRecallMutation,
  useGetHondaPartQuery,
  useLazyGetHondaPartQuery,
  useGetHondaRecallsByVinQuery,
  useGenerateHondaMonthlyReportMutation,
} = promptBridgeExtendedApi;
